import React, { useState } from 'react';
import {
    Box,
    Container,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Stack,
    Divider,
    useTheme
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../../contexts/AuthContext';

function UserAuth({ compact = false }) {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const { loginWithGoogle } = useAuth();

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        setError('');

        try {
            await loginWithGoogle();
            // Logowanie zakończone sukcesem, AuthContext zaaktualizuje currentUser
        } catch (error) {
            console.error('Błąd logowania Google:', error);
            setError('Błąd podczas logowania przez Google. Spróbuj ponownie.');
        } finally {
            setIsLoading(false);
        }
    };

    if (compact) {
        return (
            <Button
                variant="contained"
                size="small"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} /> : <GoogleIcon />}
                sx={{
                    backgroundColor: '#4285f4',
                    borderRadius: '12px',
                    px: 2,
                    py: 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        backgroundColor: '#3367d6',
                        transform: 'translateY(-1px)'
                    },
                    '&:disabled': {
                        backgroundColor: theme.palette.action.disabledBackground,
                    }
                }}
            >
                {isLoading ? 'Logowanie...' : 'Zaloguj się'}
            </Button>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: { xs: '100%', sm: 400 },
            mx: 'auto'
        }}>
            {/* Kompaktowy widok na mobile - tylko przycisk */}
            <Box sx={{
                display: { xs: 'block', sm: 'none' },
                width: '100%'
            }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 1, borderRadius: '8px', fontSize: '0.75rem' }}>
                        {error}
                    </Alert>
                )}
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleGoogleLogin}
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={16} /> : <GoogleIcon />}
                    sx={{
                        py: 1,
                        backgroundColor: '#4285f4',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: '#3367d6',
                        },
                        '&:disabled': {
                            backgroundColor: theme.palette.action.disabledBackground,
                        }
                    }}
                >
                    {isLoading ? 'Logowanie...' : 'Zaloguj się przez Google'}
                </Button>
            </Box>

            {/* Pełny widok na desktop */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: 4,
                        width: '100%',
                        backgroundColor: 'rgba(17, 24, 39, 0.8)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderRadius: '16px'
                    }}
                >
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography
                            variant="h5"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontSize: '2rem',
                                fontWeight: 600,
                                color: 'white',
                                mb: 2
                            }}
                        >
                            Witaj w playLorcana!
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgb(196, 181, 253)',
                                fontSize: '1rem',
                                lineHeight: 1.4
                            }}
                        >
                            Zaloguj się, aby korzystać z pełnych funkcji aplikacji
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                            {error}
                        </Alert>
                    )}

                    <Stack spacing={2}>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={handleGoogleLogin}
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} /> : <GoogleIcon />}
                            sx={{
                                py: 1.5,
                                backgroundColor: '#4285f4',
                                borderRadius: '12px',
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#3367d6',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(66, 133, 244, 0.4)'
                                },
                                '&:disabled': {
                                    backgroundColor: theme.palette.action.disabledBackground,
                                }
                            }}
                        >
                            {isLoading ? 'Logowanie...' : 'Zaloguj się przez Google'}
                        </Button>

                        <Divider sx={{ my: 2 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgb(156, 163, 175)',
                                    fontSize: '0.75rem'
                                }}
                            >
                                lub
                            </Typography>
                        </Divider>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgb(156, 163, 175)',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4
                                }}
                            >
                                Nie masz konta? Logowanie przez Google automatycznie utworzy nowe konto.
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </Box>
        </Box>
    );
}

export default UserAuth;
